/**
 * Native sign-in against a Cognito user pool, with no hosted UI and no SDK (D52 addendum,
 * 2026-09-27).
 *
 * **Every call here is one of Cognito's public, unauthenticated operations** — `SignUp`,
 * `InitiateAuth`, `RespondToAuthChallenge`, the confirmation and reset pairs,
 * `RevokeToken`. They take a public client id and no AWS signature, so this is a JSON
 * POST and nothing more. The pool's own protections still apply to every one of them:
 * `preventUserExistenceErrors` (a wrong email and a wrong password fail identically), its
 * lockout after repeated failures, and its per-operation quotas.
 *
 * **What does not change**: the tokens. They come from the same pool through the same
 * pre-token trigger, so the access token carries the same claims the managed-login flow
 * produced and the API cannot tell the two apart.
 */

import { type SrpCrypto, ephemeral, passwordClaim } from './cognito-srp';

export type { SrpCrypto } from './cognito-srp';

export interface CognitoPool {
  /** `us-east-2_AbCdEf123` — the region and the SRP pool name are both read from it. */
  userPoolId: string;
  /** A public app client that allows `USER_SRP_AUTH`. */
  clientId: string;
  crypto: SrpCrypto;
  fetch?: typeof globalThis.fetch;
  /** Injected for tests; the signature covers it. */
  now?: () => Date;
}

/**
 * A refusal from Cognito, by its exception name.
 *
 * `code` is the name without its namespace (`NotAuthorizedException`), because that is
 * what callers branch on: `UserNotConfirmedException` routes to the code screen,
 * everything else is a message.
 */
export class CognitoError extends Error {
  constructor(
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'CognitoError';
  }
}

export interface Tokens {
  accessToken: string;
  idToken?: string;
  /** Absent on a refresh: Cognito keeps the one you already hold. */
  refreshToken?: string;
  /** Seconds. */
  expiresIn: number;
}

export type SignInResult =
  | { status: 'signed-in'; tokens: Tokens }
  /**
   * The account has an authenticator app enrolled (the pool's MFA is optional, TOTP only).
   * `submit` is bound to Cognito's challenge session, which lasts three minutes.
   */
  | { status: 'totp'; submit: (code: string) => Promise<Tokens> };

interface AuthenticationResult {
  AccessToken?: string;
  IdToken?: string;
  RefreshToken?: string;
  ExpiresIn?: number;
}

interface AuthResponse {
  AuthenticationResult?: AuthenticationResult;
  ChallengeName?: string;
  ChallengeParameters?: Record<string, string>;
  Session?: string;
}

function regionOf(userPoolId: string): string {
  const region = userPoolId.split('_')[0];
  if (!region || !userPoolId.includes('_')) throw new Error(`Not a user pool id: ${userPoolId}`);
  return region;
}

async function call<T>(pool: CognitoPool, operation: string, body: Record<string, unknown>): Promise<T> {
  const fetchImpl = pool.fetch ?? globalThis.fetch;
  const response = await fetchImpl(`https://cognito-idp.${regionOf(pool.userPoolId)}.amazonaws.com/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${operation}`,
    },
    body: JSON.stringify(body),
  });
  const text = await response.text();
  const parsed = (text ? JSON.parse(text) : {}) as Record<string, unknown>;
  if (!response.ok) {
    // `__type` is sometimes namespaced (`com.amazonaws…#NotAuthorizedException`).
    const code =
      String(parsed.__type ?? `HTTP${response.status}`)
        .split('#')
        .pop() ?? 'Unknown';
    throw new CognitoError(code, String(parsed.message ?? parsed.Message ?? code));
  }
  return parsed as T;
}

function tokensFrom(result: AuthenticationResult | undefined): Tokens {
  if (!result?.AccessToken) throw new CognitoError('NoTokens', 'Cognito returned no tokens');
  return {
    accessToken: result.AccessToken,
    ...(result.IdToken ? { idToken: result.IdToken } : {}),
    ...(result.RefreshToken ? { refreshToken: result.RefreshToken } : {}),
    // Always sent; the fallback keeps a missing one from meaning "already expired".
    expiresIn: result.ExpiresIn ?? 3600,
  };
}

/**
 * Create an account. `confirmed` is true when no code is coming — a `@going-once.test`
 * identity outside prod confirms itself in the pre-sign-up trigger.
 */
export async function signUp(
  pool: CognitoPool,
  input: {
    email: string;
    password: string;
    /** The `name` attribute, which becomes the account's display name. */
    name?: string;
    /** Passed to the sign-up triggers; a first Google sign-in carries its ID token here (GO-568). */
    clientMetadata?: Record<string, string>;
  },
): Promise<{ confirmed: boolean }> {
  const result = await call<{ UserConfirmed?: boolean }>(pool, 'SignUp', {
    ClientId: pool.clientId,
    Username: input.email,
    Password: input.password,
    UserAttributes: [
      { Name: 'email', Value: input.email },
      ...(input.name ? [{ Name: 'name', Value: input.name }] : []),
    ],
    ...(input.clientMetadata ? { ClientMetadata: input.clientMetadata } : {}),
  });
  return { confirmed: Boolean(result.UserConfirmed) };
}

export async function confirmSignUp(pool: CognitoPool, input: { email: string; code: string }): Promise<void> {
  await call(pool, 'ConfirmSignUp', { ClientId: pool.clientId, Username: input.email, ConfirmationCode: input.code });
}

export async function resendConfirmation(pool: CognitoPool, input: { email: string }): Promise<void> {
  await call(pool, 'ResendConfirmationCode', { ClientId: pool.clientId, Username: input.email });
}

export async function forgotPassword(pool: CognitoPool, input: { email: string }): Promise<void> {
  await call(pool, 'ForgotPassword', { ClientId: pool.clientId, Username: input.email });
}

export async function confirmForgotPassword(
  pool: CognitoPool,
  input: { email: string; code: string; password: string },
): Promise<void> {
  await call(pool, 'ConfirmForgotPassword', {
    ClientId: pool.clientId,
    Username: input.email,
    ConfirmationCode: input.code,
    Password: input.password,
  });
}

/**
 * `USER_SRP_AUTH`: send `A`, answer the `PASSWORD_VERIFIER` challenge, collect tokens —
 * or hand back the TOTP step for an account that enrolled one.
 */
export async function signIn(pool: CognitoPool, input: { email: string; password: string }): Promise<SignInResult> {
  const poolName = pool.userPoolId.split('_')[1] ?? '';
  const secret = ephemeral(pool.crypto);

  const started = await call<AuthResponse>(pool, 'InitiateAuth', {
    ClientId: pool.clientId,
    AuthFlow: 'USER_SRP_AUTH',
    // Unpadded, as the reference client sends it; the hashes pad it themselves.
    AuthParameters: { USERNAME: input.email, SRP_A: secret.A.toString(16) },
  });
  if (started.ChallengeName !== 'PASSWORD_VERIFIER' || !started.ChallengeParameters) {
    throw new CognitoError('UnexpectedChallenge', `Expected PASSWORD_VERIFIER, got ${started.ChallengeName ?? 'none'}`);
  }

  const parameters = started.ChallengeParameters;
  const userIdForSrp = parameters.USER_ID_FOR_SRP ?? '';
  const claim = await passwordClaim(pool.crypto, {
    poolName,
    userIdForSrp,
    password: input.password,
    ephemeral: secret,
    srpB: parameters.SRP_B ?? '',
    salt: parameters.SALT ?? '',
    secretBlock: parameters.SECRET_BLOCK ?? '',
    now: (pool.now ?? (() => new Date()))(),
  });

  const answered = await call<AuthResponse>(pool, 'RespondToAuthChallenge', {
    ClientId: pool.clientId,
    ChallengeName: 'PASSWORD_VERIFIER',
    ChallengeResponses: { USERNAME: userIdForSrp, ...claim },
    ...(started.Session ? { Session: started.Session } : {}),
  });

  if (answered.AuthenticationResult) return { status: 'signed-in', tokens: tokensFrom(answered.AuthenticationResult) };

  if (answered.ChallengeName === 'SOFTWARE_TOKEN_MFA') {
    return {
      status: 'totp',
      submit: async (code) => {
        const done = await call<AuthResponse>(pool, 'RespondToAuthChallenge', {
          ClientId: pool.clientId,
          ChallengeName: 'SOFTWARE_TOKEN_MFA',
          ChallengeResponses: { USERNAME: userIdForSrp, SOFTWARE_TOKEN_MFA_CODE: code },
          Session: answered.Session,
        });
        return tokensFrom(done.AuthenticationResult);
      },
    };
  }

  // NEW_PASSWORD_REQUIRED is an admin-created user, and nobody signs up that way — the
  // triggers would not have run (`scripts/lib/token.ts` says why). Anything else is a
  // pool setting this client was not written for; naming it is the useful failure.
  throw new CognitoError('UnexpectedChallenge', `Sign-in needs ${answered.ChallengeName ?? 'an unknown step'}`);
}

/**
 * `CUSTOM_AUTH`, answered with a provider's ID token (GO-568) — the way a social sign-in
 * reaches the pool now there is no hosted page (D35 addendum). Today that is Google, for
 * staff, on the staff client; the pool's `verify-auth-challenge` trigger decides.
 *
 * `email` names the account. It comes from the same Google token, so a mismatch is the
 * trigger's to refuse rather than this function's to notice.
 *
 * **A refused answer is thrown, not retried.** The trigger allows three tries, and a
 * refusal comes back as the challenge again rather than as an error, but resending the
 * same token cannot get a different answer. `ProviderTokenRefused` is the code for it.
 */
export async function signInWithIdToken(pool: CognitoPool, input: { email: string; idToken: string }): Promise<Tokens> {
  const started = await call<AuthResponse>(pool, 'InitiateAuth', {
    ClientId: pool.clientId,
    AuthFlow: 'CUSTOM_AUTH',
    AuthParameters: { USERNAME: input.email },
  });
  if (started.ChallengeName !== 'CUSTOM_CHALLENGE') {
    throw new CognitoError('UnexpectedChallenge', `Expected CUSTOM_CHALLENGE, got ${started.ChallengeName ?? 'none'}`);
  }

  const answered = await call<AuthResponse>(pool, 'RespondToAuthChallenge', {
    ClientId: pool.clientId,
    ChallengeName: 'CUSTOM_CHALLENGE',
    // The pool's own username for the account (an alias pool's is not the email).
    ChallengeResponses: { USERNAME: started.ChallengeParameters?.USERNAME ?? input.email, ANSWER: input.idToken },
    ...(started.Session ? { Session: started.Session } : {}),
  });
  if (answered.AuthenticationResult) return tokensFrom(answered.AuthenticationResult);
  throw new CognitoError('ProviderTokenRefused', 'The sign-in was refused');
}

/** A new access token from the refresh token. Cognito does not rotate it on this pool. */
export async function refresh(pool: CognitoPool, refreshToken: string): Promise<Tokens> {
  const result = await call<AuthResponse>(pool, 'InitiateAuth', {
    ClientId: pool.clientId,
    AuthFlow: 'REFRESH_TOKEN_AUTH',
    AuthParameters: { REFRESH_TOKEN: refreshToken },
  });
  return tokensFrom(result.AuthenticationResult);
}

/**
 * Revoke the refresh token and every access token minted from it. The client has
 * `enableTokenRevocation`, so this is a real sign-out rather than a forgotten token.
 */
export async function revoke(pool: CognitoPool, refreshToken: string): Promise<void> {
  await call(pool, 'RevokeToken', { ClientId: pool.clientId, Token: refreshToken });
}

/** The pool's password policy (`infra/identity-stack.ts`), said the way a person reads it. */
export const PASSWORD_RULE = 'At least 12 characters, including a lowercase letter.';

/**
 * What to tell a person about a failed sign-in step, in our words rather than Cognito's.
 *
 * Shared by both clients' sign-in screens, so the phone and the web say the same thing.
 * Cognito's messages are written for developers (`Password did not conform with policy`),
 * and one of them is deliberately the same for a wrong password and an unknown email
 * (`preventUserExistenceErrors`), which this copy must not undo by guessing which it was.
 *
 * `undefined` means this cannot explain it: the caller reports it and shows a generic line.
 */
export function signInProblem(error: unknown): string | undefined {
  if (error instanceof CognitoError) {
    switch (error.code) {
      case 'NotAuthorizedException':
        // Also Cognito's lockout, which it signals with the same exception.
        return /attempts/i.test(error.message)
          ? 'Too many attempts. Wait a few minutes and try again.'
          : "That email and password don't match.";
      case 'UsernameExistsException':
        return 'There is already an account with this email. Sign in instead.';
      case 'InvalidPasswordException':
        return PASSWORD_RULE;
      case 'CodeMismatchException':
        return "That code isn't right. Check the most recent email.";
      case 'ExpiredCodeException':
        return 'That code has expired. Send a new one.';
      case 'LimitExceededException':
      case 'TooManyRequestsException':
      case 'TooManyFailedAttemptsException':
        return 'Too many attempts. Wait a few minutes and try again.';
      case 'InvalidParameterException':
        // Cognito's catch-all for a malformed request: an empty or badly formed field,
        // which the screens mostly catch first. It does not say which field.
        return 'Check the email and password.';
    }
  }
  // `fetch` rejects with a TypeError when there is no network at all.
  if (error instanceof TypeError) return "Couldn't reach Going Once. Check your connection.";
  return undefined;
}
